import type {
  IRouterPage,
  ComponentRef,
} from '@wix/public-editor-platform-interfaces';
import type { RouterRef } from '@wix/document-services-types';
import type { PlatformPrivateServices } from '../../PlatformPrivateServices';
import { PageHandle } from './PageHandle';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class RouterPageHandle extends PageHandle implements IRouterPage {
  #routerEditorPointer: EditorPointer<RouterRef>;

  constructor(
    ps: PlatformPrivateServices,
    pageEditorPointer: EditorPointer<ComponentRef>,
    routerEditorPointer: EditorPointer<RouterRef>,
  ) {
    super(ps, pageEditorPointer);
    this.#routerEditorPointer = routerEditorPointer;
  }

  #getRouterRef(): RouterRef {
    const ref =
      this.ps.platformPackagesBridge.EditorPointerAPI?.getDocumentPointer(
        this.#routerEditorPointer,
      );
    if (!ref) {
      throw new Error('EditorPointerAPI is not available');
    }
    return ref;
  }

  #getRouterData() {
    const routerData = this.ps.ds.routers.get.byRef(this.#getRouterRef());
    if (!routerData) {
      throw new Error('Router not found');
    }
    return routerData;
  }

  async isConnected(): Promise<boolean> {
    return Object.values(this.#getRouterData().pages ?? {}).includes(
      this.getRef().id,
    );
  }

  async getPageRole(): Promise<string> {
    if (!(await this.isConnected())) {
      throw new Error('Page is not connected to this router');
    }
    const pageId = this.getRef().id;
    const entry = Object.entries(this.#getRouterData().pages ?? {}).find(
      ([, id]) => id === pageId,
    )!;
    return entry[0];
  }

  async updatePageRole(pageRole: string): Promise<void> {
    if (!(await this.isConnected())) {
      throw new Error('Cannot update role of a disconnected page');
    }
    const routerRef = this.#getRouterRef();
    const pageRef = this.getRef();
    // TODO(EP-8590): DS types declare disconnect() with no args; the runtime accepts (routerRef, pageRef). Remove this workaround once the upstream type is fixed.
    // @ts-expect-error disconnect args not yet typed in DocumentServicesObject
    await this.ps.ds.routers.pages.disconnect(routerRef, pageRef);
    await this.ps.ds.routers.pages.connect(routerRef, pageRef, [pageRole], {});
    await this.ps.ds.waitForChangesAppliedAsync();
  }

  async connect(pageRole: string): Promise<void> {
    if (await this.isConnected()) {
      throw new Error('Page is already connected to this router');
    }
    await this.ps.ds.routers.pages.connect(
      this.#getRouterRef(),
      this.getRef(),
      [pageRole],
      {},
    );
    const { appDefinitionId } = this.#getRouterData();
    this.ps.ds.pages.data.update(this.getRef().id, {
      pageUriSEO: '',
      parentPageId: undefined,
      managingAppDefId: appDefinitionId,
    });
    await this.ps.ds.waitForChangesAppliedAsync();
  }

  async disconnect(): Promise<void> {
    if (!(await this.isConnected())) {
      throw new Error('Page is not connected to this router');
    }
    const routerRef = this.#getRouterRef();
    const pageRef = this.getRef();
    // TODO(EP-8590): DS types declare disconnect() with no args; the runtime accepts (routerRef, pageRef). Remove this workaround once the upstream type is fixed.
    // @ts-expect-error disconnect args not yet typed in DocumentServicesObject
    await this.ps.ds.routers.pages.disconnect(routerRef, pageRef);
    await this.ps.ds.waitForChangesAppliedAsync();
  }

  asIRouterPage(): IRouterPage {
    return Object.assign(super.asIPage(), {
      getPageRole: () => this.getPageRole(),
      updatePageRole: (role: string) => this.updatePageRole(role),
      isConnected: () => this.isConnected(),
      connect: (pageRole: string) => this.connect(pageRole),
      disconnect: () => this.disconnect(),
    });
  }
}
